// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useRef, useCallback } from 'react';
import { Alert, AppIcon, Button, TextArea, ProgressBar } from '@loa/ui-components';
import type {
  PolicyDocumentUploadResult,
  PolicyDocumentError,
  UploadProgress
} from '../types/policyDocument';
import {
  ALLOWED_DOCUMENT_TYPES,
  MAX_FILE_SIZE,
  formatFileSize,
  getFileIcon
} from '../types/policyDocument';
import { policyDocumentApi } from '../api/policyDocumentApi';
import './PolicyDocumentUploader.css';

type PolicyDocumentUploaderProps = Readonly<{
  policyId: string;
  onUploadComplete: (result: PolicyDocumentUploadResult) => void;
  onUploadError: (error: PolicyDocumentError) => void;
  onBehalfOfAdvisorId?: string;
  advisorName?: string;
  disabled?: boolean;
  maxFileSize?: number;
  acceptedFileTypes?: string[];
}>;

type UploadState = 'idle' | 'dragover' | 'validating' | 'uploading' | 'success' | 'error';

export function PolicyDocumentUploader({
  policyId,
  onUploadComplete,
  onUploadError,
  onBehalfOfAdvisorId,
  advisorName,
  disabled = false,
  maxFileSize = MAX_FILE_SIZE,
  acceptedFileTypes = [...ALLOWED_DOCUMENT_TYPES]
}: PolicyDocumentUploaderProps) {
  const [state, setState] = useState<UploadState>('idle');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [changeSummary, setChangeSummary] = useState('');
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = useCallback((file: File): string | null => {
    // Check file size
    if (file.size > maxFileSize) {
      return `File size exceeds the maximum allowed (${formatFileSize(maxFileSize)}). Please choose a smaller file or compress the document.`;
    }

    // Check file type
    if (!acceptedFileTypes.includes(file.type)) {
      return 'This file type is not supported. Please upload a PDF, image, Excel, Word, or CSV file.';
    }

    return null;
  }, [maxFileSize, acceptedFileTypes]);

  const handleFileSelect = useCallback((file: File) => {
    setState('validating');
    setErrorMessage('');

    const validationError = validateFile(file);
    if (validationError) {
      setState('error');
      setErrorMessage(validationError);
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
    setState('idle');
  }, [validateFile]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!disabled && state !== 'uploading') {
      setState('dragover');
    }
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (state === 'dragover') {
      setState(selectedFile ? 'idle' : 'idle');
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (disabled || state === 'uploading') {
      return;
    }

    const file = event.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDropzoneClick = () => {
    if (!disabled && state !== 'uploading') {
      fileInputRef.current?.click();
    }
  };

  const handleDropzoneKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if ((event.key === 'Enter' || event.key === ' ') && !disabled && state !== 'uploading') {
      event.preventDefault();
      fileInputRef.current?.click();
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setState('uploading');
    setUploadProgress(null);
    setErrorMessage('');

    try {
      let result: PolicyDocumentUploadResult;

      if (onBehalfOfAdvisorId) {
        result = await policyDocumentApi.uploadDocumentOnBehalf(
          policyId,
          onBehalfOfAdvisorId,
          selectedFile,
          changeSummary || undefined,
          (progress) => setUploadProgress(progress)
        );
      } else {
        result = await policyDocumentApi.uploadDocument(
          policyId,
          selectedFile,
          changeSummary || undefined,
          (progress) => setUploadProgress(progress)
        );
      }

      setState('success');
      onUploadComplete(result);

      // Auto-reset after success
      setTimeout(() => {
        setSelectedFile(null);
        setChangeSummary('');
        setUploadProgress(null);
        setState('idle');
      }, 3000);

    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string; code?: string } } };
      const message = error.response?.data?.message || 'Failed to upload document. Please try again.';
      const code = error.response?.data?.code || 'UPLOAD_ERROR';

      setState('error');
      setErrorMessage(message);
      onUploadError({ code, message });
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    setChangeSummary('');
    setUploadProgress(null);
    setErrorMessage('');
    setState('idle');
  };

  const handleRetry = () => {
    setErrorMessage('');
    if (selectedFile) {
      setState('idle');
    } else {
      setState('idle');
    }
  };

  const getDropzoneClassName = (): string => {
    const classes = ['document-dropzone'];
    if (state === 'dragover') classes.push('document-dropzone--dragover');
    if (state === 'uploading') classes.push('document-dropzone--uploading');
    if (state === 'error') classes.push('document-dropzone--error');
    if (state === 'success') classes.push('document-dropzone--success');
    if (disabled) classes.push('document-dropzone--disabled');
    return classes.join(' ');
  };

  const renderDropzoneContent = () => {
    if (state === 'success') {
      return (
        <div className="dropzone-content">
          <AppIcon name="check circle" size="huge" color="green" />
          <p className="dropzone-title">Upload Complete</p>
          <p className="dropzone-subtitle">Document uploaded successfully</p>
        </div>
      );
    }

    if (state === 'uploading') {
      return (
        <div className="dropzone-content">
          <div className="file-info">
            <AppIcon name={getFileIcon(selectedFile?.type || '') as 'file'} size="large" />
            <span className="file-name">{selectedFile?.name}</span>
          </div>
          <ProgressBar
            percent={uploadProgress?.percentage || 0}
            size="sm"
            className="upload-progress"
          />
          <p className="progress-text">{uploadProgress?.percentage || 0}% uploaded</p>
          <Button variant="outlined" size="sm" onClick={handleCancel} iconLeft={<AppIcon name="times" />}>
            Cancel
          </Button>
        </div>
      );
    }

    if (selectedFile) {
      return (
        <div className="dropzone-content">
          <div className="file-info">
            <AppIcon name={getFileIcon(selectedFile.type) as 'file'} size="large" />
            <div className="file-details">
              <span className="file-name">{selectedFile.name}</span>
              <span className="file-size">{formatFileSize(selectedFile.size)}</span>
            </div>
          </div>

          <div
            className="summary-form"
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
            onKeyDown={(e: React.KeyboardEvent) => e.stopPropagation()}
          >
            <TextArea
              placeholder="Describe this version (optional)"
              value={changeSummary}
              onChange={(e) => setChangeSummary(e.target.value)}
              onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => e.stopPropagation()}
              maxLength={500}
              rows={2}
            />
            <div className="char-count">{changeSummary.length}/500</div>
          </div>

          <div
            className="action-buttons"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <Button variant="filled" onClick={handleUpload} disabled={disabled} data-testid="upload-button" iconLeft={<AppIcon name="upload" />}>
              Upload
            </Button>
            <Button variant="outlined" onClick={handleCancel} data-testid="cancel-button" iconLeft={<AppIcon name="times" />}>
              Cancel
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="dropzone-content">
        <AppIcon name="cloud upload" size="huge" color="grey" />
        <p className="dropzone-title">
          {state === 'dragover' ? 'Drop file to upload' : 'Drag and drop your file here'}
        </p>
        <p className="dropzone-subtitle">or click to browse</p>
        <p className="dropzone-formats">
          Supported formats: PDF, XLSX, DOCX, Images, CSV<br />
          Maximum size: {formatFileSize(maxFileSize)}
        </p>
      </div>
    );
  };

  return (
    <div className="policy-document-uploader">
      {onBehalfOfAdvisorId && advisorName && (
        <Alert variant="info">
          <AppIcon name="user" />
          Uploading on behalf of <strong>{advisorName}</strong>
        </Alert>
      )}

      <div
        className={getDropzoneClassName()}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleDropzoneClick}
        onKeyDown={handleDropzoneKeyDown}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label="Upload policy document. Drag and drop a file or press Enter to browse."
        aria-describedby="upload-formats-hint"
        aria-disabled={disabled}
      >
        {renderDropzoneContent()}
      </div>

      <span id="upload-formats-hint" className="visually-hidden">
        Supported formats: PDF, XLSX, DOCX, Images, CSV. Maximum size: {formatFileSize(maxFileSize)}.
      </span>

      {state === 'error' && errorMessage && (
        <Alert variant="error" header="Upload Error">
          <p>{errorMessage}</p>
          <Button variant="outlined" size="sm" onClick={handleRetry} iconLeft={<AppIcon name="redo" />}>
            Try Again
          </Button>
        </Alert>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedFileTypes.join(',')}
        onChange={handleInputChange}
        style={{ display: 'none' }}
        aria-hidden="true"
      />
    </div>
  );
}
